import { z } from "zod";

import { assertNever } from "../errors.js";
import {
  HqlAggregationFunction,
  HqlAggregationFunctionLiteral,
} from "../hql/types.js";

export const ColumnAggregationsMapping = z.record(
  z.string(),
  z.array(HqlAggregationFunctionLiteral),
);
export type ColumnAggregationsMapping = z.infer<
  typeof ColumnAggregationsMapping
>;

export const ColumnAggregationsDefinition = z.object({
  columnAggregations: ColumnAggregationsMapping,
});

export type ColumnAggregationsDefinition = z.infer<
  typeof ColumnAggregationsDefinition
>;

export const AggregationTypeArray = z.array(HqlAggregationFunctionLiteral);
export type AggregationTypeArray = z.infer<typeof AggregationTypeArray>;

/**
 * Determines the effective column type after applying an aggregation.
 * Some aggregations preserve the original type (e.g., MIN/MAX), while others always output numbers.
 */
export function getAggregatedColumnType<T extends string>(
  originalType: T,
  aggregation: HqlAggregationFunction | null | undefined,
): T | "NUMBER" {
  if (!aggregation) {
    return originalType;
  }

  switch (aggregation) {
    // These aggregations preserve the original column type
    case "Min":
    case "Max":
    case "Median":
      return originalType;

    // These aggregations always output numbers
    case "Count":
    case "CountDistinct":
    case "Sum":
    case "Avg":
    case "StdDev":
    case "StdDevPop":
    case "Variance":
    case "VariancePop":
      return "NUMBER";

    default:
      assertNever(aggregation, aggregation);
  }
}
